import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import DicomViewer from './DwvComponent';
import { setShowList } from '../../../redux/modules/showList/slice';
import ReportForm from './reportForm';
import {
  Box, Button, Typography, Breadcrumbs, Link
} from '@mui/material';
import { resetGetParentPatient, resetGetStudies, resetGetStudiesImages } from '../../../redux/modules/report/slice';
import RadiologyList from './list';
import ViewReport from './viewReport';
import { setMainHeading } from '../../../redux/modules/mainHeading/slice';
import { USER_ROLE_DEFAULT } from '../../../constants/Constant';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

// Styled components for modern theme
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100%',
}));

const ActionBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  gap: theme.spacing(2),
  flexWrap: 'wrap',
}));

const BackButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 10,
  padding: '8px 16px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
  color: '#fff',
  borderRadius: 10,
  padding: '8px 20px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 2px 8px rgba(5, 47, 93, 0.2)',
  '&:hover': {
    backgroundColor: '#041e3d',
    boxShadow: '0 4px 12px rgba(5, 47, 93, 0.3)',
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
}));

const StyledBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
  '& .MuiBreadcrumbs-separator': {
    color: theme.palette.text.secondary,
  },
}));

const BreadcrumbLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  fontSize: '0.875rem',
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

const BreadcrumbText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
  fontSize: '0.875rem',
}));

const ImageReport = () => {
  const dispatch = useDispatch();
  const { currentBranch } = useSelector(state => state.branch);
  const { showList } = useSelector(state => state.reduxShowList);
  let { getStudiesRes, getParentPatientRes, getStudiesImagesRes } = useSelector(state => state.report);
  let { userData } = useSelector(state => state.auth);
  const [showComponent, setShowComponent] = useState(null);
  const [itemData, setItemData] = useState({});

  useEffect(() => {
    dispatch(setMainHeading("Image & Report"));
    if (getParentPatientRes) {
      if (getParentPatientRes?.success) {
        setItemData({
          ...itemData,
          studyData: getParentPatientRes?.data
            ? { ...itemData.studyData, ...getParentPatientRes.data }
            : itemData.studyData || null,
          seletedpatient: { ...itemData.seletedpatient, orthancParentPatientId: getParentPatientRes.data }
        });
      } else {
        setItemData({
          ...itemData,
          parentPatientResError: "Patient Files not Found Please Check Pac Server",
        });
      }
      dispatch(resetGetParentPatient());
    }

    if (getStudiesRes) {
      if (getStudiesRes.success) {
        setItemData({
          ...itemData,
          studyData: { ...itemData.studyData, ...getStudiesRes.data }
        });
      }
      dispatch(resetGetStudies());
    }

    if (getStudiesImagesRes) {
      if (getStudiesImagesRes.success) {
        setItemData({
          ...itemData,
          studyImages: getStudiesImagesRes.data
        });
      }
      dispatch(resetGetStudiesImages());
    }
  }, [dispatch, getParentPatientRes, getStudiesImagesRes, getStudiesRes, setShowComponent, itemData]);

  useEffect(() => {
    if (showComponent) {
      dispatch(setShowList(false));
    }
  }, [dispatch, showComponent]);

  useEffect(() => {
    if (showList) {
      setShowComponent(false);
    }
  }, [showList]);

  useEffect(() => {
    if (currentBranch) {
      setShowComponent(false);
    }
  }, [dispatch, currentBranch]);

  const handleBack = () => {
    setShowComponent(null);
  };

  const getPatientName = () => {
    if (itemData?.seletedpatient) {
      return `${itemData.seletedpatient.fName || ''} ${itemData.seletedpatient.lName || ''}`.trim() || 'Patient';
    }
    return 'Patient';
  };

  // View Files Component
  if (showComponent === "viewFiles") {
    return (
      <PageContainer>
        <ActionBar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BackButton
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Back
            </BackButton>
            <StyledBreadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
              <BreadcrumbLink onClick={handleBack}>Imaging</BreadcrumbLink>
              <BreadcrumbText>{getPatientName()}</BreadcrumbText>
              <BreadcrumbText>DICOM Viewer</BreadcrumbText>
            </StyledBreadcrumbs>
          </Box>
        </ActionBar>
        <DicomViewer itemData={itemData} setShowComponent={setShowComponent} />
      </PageContainer>
    );
  }

  // Create/Edit Report Component
  if (showComponent === "createReport") {
    return (
      <PageContainer>
        <ActionBar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BackButton
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Back
            </BackButton>
            <StyledBreadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
              <BreadcrumbLink onClick={handleBack}>Imaging</BreadcrumbLink>
              <BreadcrumbText>{getPatientName()}</BreadcrumbText>
              <BreadcrumbText>{itemData?.reportData ? 'Edit Report' : 'Create Report'}</BreadcrumbText>
            </StyledBreadcrumbs>
          </Box>
          {(userData.role !== USER_ROLE_DEFAULT.PATIENT || userData.canViewImages) && (
            <SecondaryButton
              component="a"
              href={`${process.env.REACT_APP_OHIF_VIEWER_URL || 'http://localhost:3001'}/viewer?StudyInstanceUIDs=${itemData?.studyData?.orthancStudyInstanceUID}`}
              disabled={!itemData?.studyData?.orthancStudyInstanceUID}
              target="_blank"
              startIcon={<OpenInNewIcon />}
            >
              {itemData?.studyData?.orthancStudyInstanceUID ? "Open DICOM Viewer" : "Loading..."}
            </SecondaryButton>
          )}
        </ActionBar>
        <ReportForm itemData={itemData} setShowComponent={setShowComponent} setItemData={setItemData} />
      </PageContainer>
    );
  }

  // View Report Component (Summary View)
  if (showComponent === "viewReport") {
    return (
      <PageContainer>
        <ActionBar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BackButton
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Back
            </BackButton>
            <StyledBreadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
              <BreadcrumbLink onClick={handleBack}>Imaging</BreadcrumbLink>
              <BreadcrumbText>{getPatientName()}</BreadcrumbText>
              <BreadcrumbText>Report Summary</BreadcrumbText>
            </StyledBreadcrumbs>
          </Box>
        </ActionBar>

        <ViewReport itemData={itemData} />
      </PageContainer>
    );
  }

  // Default: Study List
  return (
    <PageContainer>
      <RadiologyList setItemData={setItemData} setShowComponent={setShowComponent} itemData={itemData} />
    </PageContainer>
  );
};

export default ImageReport;
